import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import { 
  Shield, LayoutDashboard, AlertTriangle, Lightbulb, 
  Settings, LogOut, User, Sun, Moon, Menu, ChevronDown,
  TrendingUp, FileBarChart, Users, PiggyBank, Calculator,
  Clock, Tag, BarChart3, Sparkles, Search,
  TrendingDown, Zap, FolderCog, Command, Cloud, Dna,
  GitBranch, Bot, CloudSun, Building2, Monitor, Leaf, Box, FileCheck,
  FileCode, Cpu, Mic, GitPullRequest, Package, Heart, Landmark, 
  MessageSquare, Award, Trophy, Download, Blocks, SearchCode, Share2, Bell,
  Info, Briefcase, Mail, FolderOpen, Brain, Wrench, ShieldCheck, Plug,
  Timer, HardDrive, AlertOctagon, Activity, Skull, Target,
  FileText, Tags, ArrowRightLeft, Presentation, Handshake, Flame, Code2,
  Anchor, FileCheck2, Receipt, Scale, ListChecks, Repeat, Code, Layers,
  HelpCircle, Database, Power, GitMerge, Rocket, TreePine
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import OrgSwitcher from './OrgSwitcher';
import CommandPalette from './CommandPalette';
import MegaMenu from './MegaMenu';
import PilotMoatTour from './PilotMoatTour';
import UpgradeModal from './UpgradeModal';
import WhatsNewModal from './WhatsNewModal';
import { CHANGELOG_VERSION } from '../config/changelog';
import { getFeatureTier, canAccessTier, getTierBadge } from '../config/featureTiers';

const Navbar = () => {
  const { user, logout, canAccess, hasPermission } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, tier: 'pro', feature: '' });
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [hasUnseenUpdates, setHasUnseenUpdates] = useState(false);
  // Token Intel discovery — show pulsing NEW dot on the new top-level
  // mega-menu until the user has either (a) clicked it open or
  // (b) landed on any /token-*, /prompt*, /traces, /evals, /llm-proxy,
  // /saas-spend, /ai-feature-profitability, or /ai-gpu route.
  const [tokenIntelDiscovered, setTokenIntelDiscovered] = useState(
    () => localStorage.getItem('token_intel_discovered') === '1'
  );

  // Auto-mark Token Intel as discovered when user visits any of its routes
  useEffect(() => {
    if (tokenIntelDiscovered) return;
    const tokenRoutes = [
      '/prompt-playground', '/prompts', '/traces', '/evals',
      '/llm-proxy', '/token-intelligence', '/token-intelligence-suite',
      '/saas-spend', '/ai-feature-profitability', '/ai-gpu',
    ];
    if (tokenRoutes.some((r) => location.pathname.startsWith(r))) {
      localStorage.setItem('token_intel_discovered', '1');
      setTokenIntelDiscovered(true);
    }
  }, [location.pathname, tokenIntelDiscovered]);

  // Check for unseen changelog updates
  useEffect(() => {
    const seenVersion = localStorage.getItem('changelog_last_seen');
    setHasUnseenUpdates(seenVersion !== CHANGELOG_VERSION);
  }, []);

  const openWhatsNew = () => {
    setWhatsNewOpen(true);
    localStorage.setItem('changelog_last_seen', CHANGELOG_VERSION);
    setHasUnseenUpdates(false);
  };

  // Get user's selected providers and role
  const selectedProviders = JSON.parse(localStorage.getItem('selected_providers') || '[]');
  const isAdmin = user?.role === 'admin';
  const isDemoUser = user?.email === 'demo@cartieai.com';
  // Session-only flag — when set, hides the Demo Mode top banner (useful
  // when the founder is screenshotting / recording a demo and doesn't
  // want the green ribbon in the frame).
  const [demoBannerHidden, setDemoBannerHidden] = useState(
    () => sessionStorage.getItem('demo_banner_hidden') === '1',
  );
  const userTier = user?.subscription_tier || 'starter'; // Default to starter
  // Admin ALWAYS sees all menus - no filtering based on provider selection
  // Regular users see menus based on their provider selection
  const hasSnowflake = isAdmin === true || selectedProviders.includes('snowflake') || selectedProviders.length === 0;
  const hasCloudProviders = isAdmin === true || selectedProviders.some(p => ['aws', 'azure', 'gcp'].includes(p)) || selectedProviders.length === 0;
  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true';

  // Global keyboard shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleLogout = async () => {
    await logout();          // wipes tokens, creds, axios headers, server-side audit log
    navigate('/login', { replace: true });   // replace so browser back-button can't return to protected pages
  };

  // 2026 PRINCIPLES applied:
  //   1. NO duplicates — every route appears in exactly ONE place.
  //   2. Each menu has 2-3 FEATURED hero tiles + 2-4 grouped columns.
  //   3. "NEW" badge reserved for genuinely-recent (last 60d) features only.
  //   4. Snowflake-specific tools live under Cost > Snowflake (consolidated).
  //   5. AI/GPU lives ONLY under Optimize (was duplicated in Analytics+Optimize).
  const navGroups = useMemo(() => ({
    // ============ COST — Visibility, Anomalies, Forecasting, Snowflake ============
    cost: {
      label: 'Cost',
      Icon: BarChart3,
      accentColor: 'emerald',
      featured: [
        {
          path: '/anomalies',
          label: 'Anomaly Detection',
          description: 'Real-time alerts when costs spike unexpectedly.',
          icon: AlertTriangle,
          gradient: 'from-rose-500 to-pink-500',
          module: 'anomalies', action: 'view',
        },
        {
          path: '/cost-forecasting',
          label: 'Cost Forecasting',
          description: '30-day predictions with 95% accuracy.',
          icon: TrendingUp,
          gradient: 'from-emerald-500 to-teal-500',
          module: 'dashboard', action: 'view',
        },
        {
          path: '/cloud-pl',
          label: 'Cloud P&L',
          description: 'CFO-grade financial reporting.',
          icon: Building2,
          gradient: 'from-blue-500 to-cyan-500',
          feature: 'cloud_pl',
        },
      ],
      columns: [
        {
          heading: 'Visibility',
          items: [
            { path: '/tech-spend', label: 'Total Tech Spend', icon: Monitor, feature: 'tech_spend' },
            { path: '/customer-pl', label: 'Customer P&L (Stripe)', icon: Users, feature: 'customer_economics' },
            { path: '/pr-cost-predictor', label: 'PR-Time Cost (Terraform)', icon: Monitor, feature: 'tech_spend' },
            // v35.19 — /customer-economics consolidated into /customer-pl + Bill Detective Per-Customer tab
            { path: '/cost-dna', label: 'Cost DNA', icon: Dna, module: 'dashboard', action: 'view' },
            { path: '/cost-graph', label: 'Cost Graph', icon: Share2, module: 'dashboard', action: 'view' },
            { path: '/health-score', label: 'Cost Health Score', icon: Award, module: 'dashboard', action: 'view' },
            { path: '/industry', label: 'Industry Intel', icon: Landmark, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Analysis & Forecast',
          items: [
            { path: '/comparison', label: 'Compare Periods', icon: TrendingUp, module: 'dashboard', action: 'view' },
            { path: '/weather', label: 'Cost Weather', icon: CloudSun, module: 'dashboard', action: 'view' },
            { path: '/search', label: 'AI Search', icon: SearchCode, module: 'dashboard', action: 'view' },
            { path: '/ai-command-center', label: 'AI Command Center', icon: Brain, module: 'ai_advisor', action: 'view' },
            { path: '/reports', label: 'Reports', icon: FileBarChart, module: 'reports', action: 'view' },
            { path: '/export', label: 'Export Center', icon: Download, module: 'reports', action: 'view' },
          ],
        },
        {
          heading: 'Snowflake',
          items: [
            { path: '/snowflake', label: 'Snowflake Dashboard', icon: Cloud, module: 'dashboard', action: 'view' },
            { path: '/snowflake-queries', label: 'Query Explorer', icon: SearchCode, module: 'dashboard', action: 'view' },
            { path: '/snowflake-alerts', label: 'Credit Alerts', icon: Bell, module: 'dashboard', action: 'view' },
            { path: '/spike-autopsy', label: 'Spike Autopsy', icon: AlertTriangle, module: 'dashboard', action: 'view' },
            { path: '/query-estimator', label: 'Query Estimator', icon: Calculator, module: 'dashboard', action: 'view' },
            { path: '/storage-detective', label: 'Storage Detective', icon: HardDrive, module: 'dashboard', action: 'view' },
            { path: '/team-chargeback', label: 'Snowflake Chargeback', icon: Users, module: 'dashboard', action: 'view' },
            { path: '/budget-countdown', label: 'Budget Countdown', icon: Timer, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Databricks & DigitalOcean',
          items: [
            { path: '/databricks', label: 'Databricks Dashboard', icon: Flame, module: 'dashboard', action: 'view', badge: 'NEW' },
            { path: '/digitalocean', label: 'DigitalOcean Dashboard', icon: Anchor, module: 'dashboard', action: 'view', badge: 'NEW' },
          ],
        },
      ],
      footer: { path: '/dashboard', label: 'Back to dashboard' },
    },

    // ============ OPTIMIZE — The 13 Killer Features + Tools + Automation ============
    optimize: {
      label: 'Optimize',
      Icon: Zap,
      accentColor: 'emerald',
      featured: [
        {
          path: '/idle-killer',
          label: 'Idle Resource Optimizer',
          description: 'Finds idle EBS/EC2/RDS/NAT/snapshots in your AWS account and lets you clean them up with 5-gate email-2FA safety.',
          icon: Skull,
          gradient: 'from-emerald-500 to-cyan-500',
          badge: 'NEW',
          module: 'costs', action: 'view',
        },
        {
          path: '/admin/payback-filter',
          label: 'Payback Filter',
          description: 'Kills recommendations whose engineering effort outweighs first-year savings.',
          icon: Scale,
          gradient: 'from-amber-500 to-orange-500',
          badge: 'NEW',
          module: 'costs', action: 'view',
        },
        {
          path: '/invoice-autopsy',
          label: 'Invoice Autopsy',
          description: 'Upload any cloud bill — AI explains every spike in plain English.',
          icon: FileText,
          gradient: 'from-emerald-500 to-cyan-500',
          badge: 'Popular',
          module: 'costs', action: 'view',
        },
      ],
      columns: [
        {
          heading: 'Killer Tools',
          items: [
            { path: '/bill-detective', label: 'Bill Detective', icon: Search, module: 'costs', action: 'view', badge: 'NEW' },
            { path: '/wall-of-fame', label: 'Wall of Fame', icon: Trophy, module: 'costs', action: 'view', badge: 'NEW' },
            // v35.19 — /cost-scorecard consolidated into /bill-detective Engineer Leaderboard tab
            { path: '/saas-detective', label: 'SaaS Detective', icon: Search, module: 'costs', action: 'view' },
            { path: '/smart-tags', label: 'Smart Tag Fixer', icon: Tags, module: 'costs', action: 'view' },
            { path: '/egress-shield', label: 'Egress Shield', icon: ArrowRightLeft, module: 'costs', action: 'view' },
            { path: '/board-deck', label: 'Board Deck Generator', icon: Presentation, module: 'dashboard', action: 'view' },
            { path: '/vendor-deals', label: 'Vendor Deal Intel', icon: Handshake, module: 'costs', action: 'view' },
            { path: '/compliance-mapper', label: 'Compliance Mapper', icon: ShieldCheck, module: 'dashboard', action: 'view' },
            { path: '/carbon', label: 'Carbon + ESG', icon: Leaf, feature: 'cloud_pl' },
            { path: '/unit-economics', label: 'Unit Economics', icon: Calculator, module: 'costs', action: 'view' },
            { path: '/commitment-intel', label: 'Commitment Intel', icon: Target, module: 'costs', action: 'view' },
          ],
        },
        {
          heading: 'AI Tools',
          items: [
            { path: '/recommendations', label: 'Recommendations', icon: Lightbulb, module: 'recommendations', action: 'view' },
            { path: '/cost-explainer', label: 'AI Explainer', icon: Sparkles, module: 'dashboard', action: 'view' },
            { path: '/voice-assistant', label: 'Voice FinOps', icon: Mic, module: 'dashboard', action: 'view' },
            { path: '/terraform-preview', label: 'Terraform Preview', icon: FileCode, module: 'costs', action: 'view' },
            { path: '/calculator', label: 'What-If Calculator', icon: Calculator, module: 'dashboard', action: 'view' },
            { path: '/peer-benchmark', label: 'Peer Benchmark', icon: Users, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Automation & Safety',
          items: [
            { path: '/pilot', label: 'Auto Pilot', icon: Bot, feature: 'autopilot' },
            { path: '/idle-killer', label: 'Idle Resource Optimizer', icon: Skull, module: 'costs', action: 'view', badge: 'NEW' },
            { path: '/scheduler', label: 'Resource Scheduler', icon: Clock, module: 'settings', action: 'view' },
            { path: '/time-machine', label: 'Time Machine', icon: Clock, feature: 'time_machine' },
            { path: '/savings', label: 'Savings Tracker', icon: PiggyBank, module: 'recommendations', action: 'view' },
            { path: '/spend-freeze', label: 'Spend Freeze', icon: AlertOctagon, module: 'settings', action: 'view' },
            { path: '/cost-conscience', label: 'Cost Conscience', icon: Brain, module: 'settings', action: 'view' },
            { path: '/ai-cfo', label: 'AI CFO', icon: Briefcase, module: 'dashboard', action: 'view' },
          ],
        },
      ],
      footer: { path: '/recommendations', label: 'See all recommendations' },
    },

    // ============ TOKEN INTEL — LLM Engineering Suite + Token P&L (the second heart) ============
    tokenIntel: {
      label: 'Token Intel',
      Icon: Brain,
      accentColor: 'emerald',
      featured: [
        {
          path: '/prompt-playground',
          label: 'Prompt Playground',
          description: 'Side-by-side cost across GPT-4o, Claude, Gemini.',
          icon: Sparkles,
          gradient: 'from-cyan-500 to-teal-500',
          badge: 'NEW',
          module: 'dashboard', action: 'view',
        },
        {
          path: '/traces',
          label: 'Trace Viewer',
          description: 'Every LLM call traced with $ per span (OTEL + proxy).',
          icon: Activity,
          gradient: 'from-cyan-500 to-blue-500',
          module: 'dashboard', action: 'view',
        },
        {
          path: '/llm-proxy',
          label: 'LLM Proxy',
          description: 'Drop-in OpenAI-compatible. Auto-route, cache, retry.',
          icon: ArrowRightLeft,
          gradient: 'from-teal-500 to-emerald-500',
          module: 'dashboard', action: 'view',
        },
      ],
      columns: [
        {
          heading: 'Engineering',
          items: [
            { path: '/prompts', label: 'Prompt Library', icon: FolderOpen, module: 'dashboard', action: 'view' },
            { path: '/evals', label: 'Eval Runner', icon: Award, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Token Economics',
          items: [
            { path: '/token-intelligence', label: 'Token Intelligence', icon: Brain, module: 'dashboard', action: 'view' },
            { path: '/ai-feature-profitability', label: 'AI Feature P&L', icon: Bot, feature: 'customer_economics' },
            { path: '/ai-gpu', label: 'AI / GPU Intelligence', icon: Cpu, module: 'costs', action: 'view' },
            { path: '/saas-spend', label: 'SaaS Spend (AI connectors)', icon: Package, module: 'dashboard', action: 'view' },
          ],
        },
      ],
      footer: { path: '/token-intelligence-suite', label: 'Open the full Token Intelligence Suite' },
    },

    // ============ SMART ENGINES — 26 Patent-Pending Sub-systems (Feb 2026) ============
    inventions: {
      label: 'Smart Engines',
      Icon: Sparkles,
      accentColor: 'emerald',
      featured: [
        {
          path: '/commitment-rebalancer',
          label: 'Commitment Rebalancer',
          description: 'Continuous rolling-window rebalancer for Savings Plans / Reserved Instances — proprietary commitment-math engine.',
          icon: Repeat,
          gradient: 'from-emerald-500 to-cyan-500',
          badge: 'NEW',
        },
        {
          path: '/k8s-cost',
          label: 'K8s Shapley Attribution',
          description: 'Monte-Carlo Shapley for shared K8s overhead. Mathematically fair, unlike proportional-share tools.',
          icon: Box,
          gradient: 'from-emerald-500 to-teal-500',
        },
        {
          path: '/causal-bot',
          label: 'Causal FinOps Bot',
          description: 'Natural-language "why did the bill spike?" — chains Cost-Bisect → Causality → P&L automatically.',
          icon: MessageSquare,
          gradient: 'from-emerald-500 to-green-500',
        },
      ],
      columns: [
        {
          heading: 'Causality',
          items: [
            { path: '/cost-bisect', label: 'Cost-Bisect', icon: GitBranch, module: 'dashboard', action: 'view' },
            { path: '/cost-causality', label: 'Cost Causality', icon: TrendingUp, module: 'dashboard', action: 'view' },
            { path: '/decision-simulator', label: 'Decision Simulator', icon: Calculator, module: 'dashboard', action: 'view' },
            { path: '/causal-bot', label: 'Causal Bot', icon: MessageSquare, module: 'dashboard', action: 'view' },
            { path: '/budget-negotiator', label: 'Budget Negotiator', icon: Handshake, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Allocation & Attribution',
          items: [
            { path: '/k8s-cost', label: 'K8s Shapley', icon: Box, module: 'costs', action: 'view' },
            { path: '/shared-cost', label: 'Shared Allocator', icon: Handshake, module: 'costs', action: 'view' },
            { path: '/reverse-tag', label: 'Reverse-Tag', icon: Tags, module: 'costs', action: 'view' },
            { path: '/tag-canon', label: 'Tag Canonicaliser', icon: ArrowRightLeft, module: 'costs', action: 'view' },
            { path: '/commitment-rebalancer', label: 'Commitment Rebalancer', icon: Repeat, module: 'costs', action: 'view', badge: 'NEW' },
            { path: '/admin/cost-centers', label: 'Cost Center Hierarchy', icon: TreePine, module: 'costs', action: 'view', badge: 'NEW' },
          ],
        },
        {
          heading: 'AI Cost & Forecast',
          items: [
            { path: '/batch-router', label: 'Batch Router', icon: ArrowRightLeft, module: 'dashboard', action: 'view' },
            { path: '/qa-failover', label: 'QA Failover', icon: ShieldCheck, module: 'dashboard', action: 'view' },
            { path: '/forward-predict', label: 'Forward Predictor', icon: TrendingUp, module: 'dashboard', action: 'view' },
            { path: '/unit-econ', label: 'Real-Time Unit Econ', icon: Activity, module: 'dashboard', action: 'view' },
            { path: '/multi-currency', label: 'Multi-Currency P&L', icon: Landmark, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Sustainability & Autonomy',
          items: [
            { path: '/carbon-cost', label: 'Carbon-Cost Pareto', icon: Leaf, module: 'dashboard', action: 'view' },
            { path: '/scope3', label: 'Scope-3 Carbon', icon: Leaf, module: 'dashboard', action: 'view' },
            { path: '/gpu-spot', label: 'GPU Spot Bidder', icon: Cpu, module: 'dashboard', action: 'view' },
            { path: '/safe-autonomy', label: 'Safe Autonomy', icon: Shield, module: 'dashboard', action: 'view' },
            { path: '/copilot', label: 'FinOps Copilot', icon: Bot, module: 'dashboard', action: 'view' },
          ],
        },
      ],
      footer: { path: '/algorithm-correctness', label: 'Verified by mathematical tests · view the manifest →' },
    },

    // ============ PILOT — The honest-FinOps moat: ROI Pledge, Activation, Audit log ============
    pilot: {
      label: 'Pilot',
      Icon: ShieldCheck,
      accentColor: 'amber',
      featured: [
        {
          path: '/admin/anomaly-autopsy',
          label: 'Anomaly Autopsy',
          description: 'Cost spike fires → auto-bisect → causal split → payback math → one Slack message. World-first 4-step chain.',
          icon: Skull,
          gradient: 'from-rose-500 to-orange-500',
          badge: 'WORLD-FIRST',
        },
        {
          path: '/admin/pr-cost-guardrail',
          label: 'PR Cost Guardrail',
          description: 'GitHub Action that blocks PRs by engineer-hour ROI math, not just dollar thresholds. Harness has $ gates — we have value-cost ratios.',
          icon: Code,
          gradient: 'from-cyan-500 to-blue-500',
          badge: 'WORLD-FIRST',
        },
        {
          path: '/admin/roadmap-forecast',
          label: 'Roadmap-Aware Forecast',
          description: 'Inject Jira/Linear launches → confidence-banded fan chart with named event markers. No other tool does this at SaaS-feature fidelity.',
          icon: TrendingUp,
          gradient: 'from-emerald-500 to-cyan-500',
          badge: 'WORLD-FIRST',
        },
      ],
      columns: [
        {
          heading: 'Honest FinOps',
          items: [
            { path: '/roi-pledge', label: 'ROI Pledge', icon: Handshake, module: 'dashboard', action: 'view' },
            { path: '/honest-scorecard', label: 'Honest Scorecard vs Competitors', icon: Trophy, module: 'dashboard', action: 'view' },
            { path: '/admin/payback-filter', label: 'Payback Filter', icon: Scale, module: 'costs', action: 'view' },
            { path: '/algorithm-correctness', label: 'Correctness Manifest', icon: FileCheck, module: 'dashboard', action: 'view' },
          ],
        },
        {
          heading: 'Auto-Chain Pipeline',
          items: [
            { path: '/admin/anomaly-autopsy', label: 'Anomaly Autopsy', icon: Skull, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/pr-cost-guardrail', label: 'PR Cost Guardrail', icon: Code, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/roadmap-forecast', label: 'Roadmap Forecast', icon: TrendingUp, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/saas-spend', label: 'SaaS Intelligence', icon: Layers, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/agent-economics', label: 'Agent Economics', icon: Brain, roles: ['admin'], badge: 'WORLD-FIRST' },
            { path: '/admin/embedding-audit', label: 'Embedding Audit', icon: Database, roles: ['admin'], badge: 'WORLD-FIRST' },
            { path: '/admin/idle-endpoints', label: 'Idle Endpoint Detector', icon: Power, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/conversion-roi', label: 'Conversion ROI', icon: Trophy, roles: ['admin'], badge: 'WORLD-FIRST' },
            { path: '/admin/pipeline-dedup', label: 'Pipeline DAG Dedup', icon: GitMerge, roles: ['admin'], badge: 'WORLD-FIRST' },
          ],
        },
        {
          heading: 'Pilot Readiness',
          items: [
            { path: '/admin/pilot-activation', label: 'Activation Score', icon: ListChecks, roles: ['admin'] },
            { path: '/admin/hot-pilots', label: 'Hot Pilots (sales)', icon: Flame, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/hn-launch', label: 'HN Launch Sequencer', icon: Rocket, roles: ['admin'], badge: 'NEW' },
            { path: '/admin/align-kickoff', label: 'A-LIGN Audit Kickoff', icon: Handshake, roles: ['admin'] },
            { path: '/admin/cost-actuals', label: 'Cost Actuals', icon: FileCheck2, roles: ['admin'] },
            { path: '/admin/cloud-billing-sync', label: 'Cloud Billing Sync', icon: Plug, roles: ['admin'] },
            { path: '/admin/incident-log', label: 'Incident Log', icon: Receipt, roles: ['admin'] },
          ],
        },
      ],
      footer: { path: '/roi-pledge', label: 'See the binding refund contract →' },
    },

    // ============ MANAGE — Setup, Teams, Infra, Integrations, Admin ============
    manage: {
      label: 'Manage',
      Icon: FolderCog,
      accentColor: 'emerald',
      featured: [
        {
          path: '/cloud-audit',
          label: 'Free Cloud Audit',
          description: 'Connect AWS, Azure, GCP, Snowflake, Databricks or DigitalOcean in 60 seconds.',
          icon: Cloud,
          gradient: 'from-blue-500 to-cyan-500',
          badge: 'Start Here',
        },
        {
          path: '/integrations',
          label: 'Integrations Hub',
          description: 'Connect cloud accounts (AWS / GCP / Azure / Snowflake), data providers (ElectricityMaps / LaunchDarkly / PostHog / OTel), and migrate from Vantage / CloudHealth / Kubecost.',
          icon: Plug,
          gradient: 'from-emerald-500 to-teal-500',
          module: 'integrations', action: 'view',
        },
        {
          path: '/dashboard-builder',
          label: 'Dashboard Builder',
          description: 'Drag-and-drop your perfect cost dashboard.',
          icon: Blocks,
          gradient: 'from-emerald-500 to-cyan-500',
          module: 'dashboard', action: 'view',
        },
      ],
      columns: [
        {
          heading: 'Teams & Allocation',
          items: [
            { path: '/teams', label: 'Teams', icon: Users, module: 'teams', action: 'view' },
            { path: '/allocation', label: 'Cost Allocation', icon: Calculator, module: 'costs', action: 'view' },
            { path: '/collaboration', label: 'Collaboration', icon: MessageSquare, module: 'dashboard', action: 'view' },
            { path: '/tagging', label: 'Tagging', icon: Tag, module: 'costs', action: 'view' },
          ],
        },
        {
          heading: 'Infrastructure',
          items: [
            { path: '/kubernetes', label: 'Kubernetes', icon: Box, module: 'costs', action: 'view' },
            // v35.19 — /git-to-bill + /pr-cost-impact consolidated into /bill-detective Code→Cost tab
          ],
        },
        {
          heading: 'Alerts & Security',
          items: [
            { path: '/guardrails', label: 'Budget Guardrails', icon: Shield, module: 'guardrails', action: 'view' },
            { path: '/bill-shock', label: 'Bill Shock Prevention', icon: Bell, module: 'settings', action: 'view' },
            { path: '/notifications', label: 'Notifications', icon: Bell, module: 'settings', action: 'view' },
            { path: '/connect', label: 'Integrations Marketplace', icon: Plug, module: 'settings', action: 'view' },
            { path: '/cost-bot', label: 'Slack/Teams Bot', icon: Bot, module: 'settings', action: 'view' },
            { path: '/compliance', label: 'SOC 2', icon: FileCheck, roles: ['admin'] },
            { path: '/security-center', label: 'Security Center', icon: Shield, module: 'settings', action: 'view' },
          ],
        },
        {
          heading: 'Admin',
          items: [
            { path: '/analytics', label: 'Analytics Dashboard', icon: Activity, roles: ['admin'] },
            { path: '/weekly-digest', label: 'Weekly Digest', icon: Mail, roles: ['admin'] },
            { path: '/ab-testing', label: 'A/B Testing', icon: Sparkles, roles: ['admin'] },
            { path: '/enterprise-sso', label: 'Enterprise SSO', icon: Shield, roles: ['admin'] },
          ],
        },
      ],
      footer: { path: '/settings', label: 'Open Settings' },
    },
  }), []);

  // Check if user can access a nav item
  const canAccessNavItem = (item) => {
    // Admin can access everything
    if (isAdmin) return true;
    
    // Check role-based access
    if (item.roles && item.roles.length > 0) {
      if (!user || !item.roles.includes(user.role)) {
        return false;
      }
    }
    if (item.feature) {
      return canAccess(item.feature);
    }
    if (item.module && item.action) {
      return hasPermission(item.module, item.action);
    }
    return true; // Default to accessible if no permission defined
  };

  // Get all items from a mega-menu group (featured + flatten columns)
  const getAllItems = (group) => {
    const out = [];
    if (group.featured) out.push(...group.featured);
    if (group.columns) group.columns.forEach((c) => out.push(...c.items));
    return out;
  };

  // Filter items based on permissions
  const getAccessibleItems = (group) => {
    return getAllItems(group).filter((item) => canAccessNavItem(item));
  };

  // Build a permission-filtered group for the MegaMenu component
  const filterGroupForMegaMenu = (group) => {
    const featured = (group.featured || []).filter((i) => canAccessNavItem(i));
    const columns = (group.columns || [])
      .map((c) => ({
        ...c,
        items: c.items.filter((i) => canAccessNavItem(i)),
      }))
      .filter((c) => c.items.length > 0);
    return { ...group, featured, columns };
  };

  // Check if group should be visible (has at least one accessible item)
  const isGroupVisible = (group) => {
    return getAccessibleItems(group).length > 0;
  };

  const isGroupActive = (group) => {
    return getAllItems(group).some((item) => location.pathname === item.path);
  };

  // Check if user can access a feature based on tier
  const canAccessFeature = (path) => {
    if (isAdmin || isDemoUser) return true;
    const requiredTier = getFeatureTier(path);
    return canAccessTier(userTier, requiredTier);
  };

  // Render a tier-gated nav item
  const renderGatedNavItem = (item, activeColor = 'emerald') => {
    const requiredTier = getFeatureTier(item.path);
    const tierBadge = getTierBadge(requiredTier);
    const isLocked = !canAccessFeature(item.path);
    const isActive = location.pathname === item.path;

    if (isLocked) {
      return (
        <DropdownMenuItem 
          key={item.path} 
          onSelect={() => {
            setTimeout(() => {
              setUpgradeModal({ open: true, tier: requiredTier, feature: item.label });
            }, 200);
          }}
          className="flex items-center gap-2 px-2 py-1.5 cursor-pointer text-sm opacity-70 hover:opacity-100"
        >
          <item.icon className="w-4 h-4 text-slate-400" />
          <span className="flex-1 text-slate-400">{item.label}</span>
          {tierBadge && (
            <span className={`px-1.5 py-0.5 text-[11px] font-bold rounded ${
              requiredTier === 'enterprise' 
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' 
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            }`}>
              {tierBadge.badge}
            </span>
          )}
        </DropdownMenuItem>
      );
    }

    return (
      <DropdownMenuItem key={item.path} asChild>
        <Link 
          to={item.path} 
          className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer text-sm ${
            isActive ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400' : ''
          }`}
        >
          <item.icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
          <span className="flex-1">{item.label}</span>
          {item.isNew && !tierBadge && <span className="px-1 py-0.5 text-[11px] font-bold bg-emerald-500 text-white rounded">NEW</span>}
          {tierBadge && (
            <span className={`px-1.5 py-0.5 text-[11px] font-bold rounded ${
              requiredTier === 'enterprise' 
                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' 
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            }`}>
              {tierBadge.badge}
            </span>
          )}
        </Link>
      </DropdownMenuItem>
    );
  };

  const getRoleBadgeColor = (role, isDemo) => {
    if (isDemo) return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    switch (role) {
      case 'admin': return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';
      case 'finance': return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400';
      default: return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
    }
  };

  // All items for mobile menu - filtered by permissions
  const allNavItems = useMemo(() => {
    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, module: 'dashboard', action: 'view' },
      { path: '/ai-advisor', label: 'AI Advisor', icon: Sparkles, feature: 'ai_advisor' },
    ];
    
    const allGroupItems = [
      ...getAccessibleItems(navGroups.cost),
      ...getAccessibleItems(navGroups.optimize),
      ...getAccessibleItems(navGroups.tokenIntel),
      ...getAccessibleItems(navGroups.inventions),
      ...getAccessibleItems(navGroups.manage),
    ];
    
    const orgItem = { path: '/organization', label: 'Organization', icon: Building2, module: 'settings', action: 'view' };
    const settingsItem = { path: '/settings', label: 'Settings', icon: Settings, module: 'settings', action: 'view' };
    const adminItems = isAdmin ? [
      { path: '/analytics', label: 'Analytics', icon: Activity, roles: ['admin'] },
      { path: '/weekly-digest', label: 'Weekly Digest', icon: Mail, roles: ['admin'] },
      { path: '/ab-testing', label: 'A/B Testing', icon: Sparkles, roles: ['admin'] },
    ] : [];
    
    return [...baseItems, ...allGroupItems, ...adminItems, orgItem, settingsItem].filter(item => canAccessNavItem(item));
  }, [navGroups, canAccess, hasPermission, isAdmin]);

  return (
    <>
      {/* Demo Mode Banner */}
      {isDemoUser && !demoBannerHidden && (
        <div className="sticky top-0 z-[110] bg-emerald-600 bg-gradient-to-r from-emerald-600 via-emerald-500 to-cyan-600 text-white px-4 py-2" data-testid="demo-mode-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span className="text-sm font-medium">You're exploring CARTIEAI in Demo Mode</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg font-medium transition-colors" data-testid="demo-signup-btn">
                  Sign Up for Free
              </button>
              <button
                onClick={() => { sessionStorage.setItem('demo_banner_hidden', '1'); setDemoBannerHidden(true); }}
                className="text-xs text-white/80 hover:text-white transition-colors flex items-center gap-1"
                data-testid="hide-demo-banner-btn"
                title="Hide banner for this session (great for screenshots)"
              >
                Hide
              </button>
              <button onClick={() => { localStorage.removeItem('onboarding_completed'); localStorage.removeItem('selected_providers'); localStorage.removeItem('hasSeenOnboarding'); localStorage.removeItem('onboardingCompleted'); localStorage.removeItem('token'); window.location.href = '/'; }} className="text-xs text-white/70 hover:text-white transition-colors flex items-center gap-1" data-testid="exit-demo-btn">
                Exit Demo
              </button>
            </div>
          </div>
        </div>
      )}
    <nav className="sticky top-0 z-[110] bg-white/80 dark:bg-slate-900 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          
          {/* Left: Logo + Main Nav */}
          <div className="flex items-center gap-1">
            {/* Logo - CARTIE Official Logo + What's New badge */}
            <div className="flex items-center gap-2.5 mr-4 group">
              <Link to="/dashboard" className="flex items-center gap-2.5 relative" data-testid="navbar-logo-link">
                <img 
                  src="/icons/cartie-emblem.png" 
                  alt="CARTIEAI" 
                  className="w-10 h-10 object-contain group-hover:scale-105 transition-transform"
                  data-testid="navbar-logo"
                />
                <span className="hidden sm:block font-bold text-lg font-['Manrope']">
                  <span className="text-slate-900 dark:text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {/* Dashboard - Direct Link
                  v34.99 (Feb 14 2026) — Color was text-slate-400 (too dim against
                  most navbar backgrounds — founder spotted "Dashboard" word
                  going invisible). Bumped to slate-700 light / slate-200 dark
                  for readable contrast in both themes. */}
              <Link
                to="/dashboard"
                className={`flex items-center gap-1.5 px-2 xl:px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  location.pathname === '/dashboard'
                    ? 'nav-active'
                    : 'text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
                data-testid="nav-dashboard"
                title="Dashboard"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden xl:inline">Dashboard</span>
              </Link>

              {/* Cost — Visibility, Forecasting, Snowflake */}
              {isGroupVisible(navGroups.cost) && (
                <MegaMenu
                  label="Cost"
                  Icon={navGroups.cost.Icon}
                  accentColor={navGroups.cost.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.cost).featured}
                  columns={filterGroupForMegaMenu(navGroups.cost).columns}
                  footer={navGroups.cost.footer}
                  testId="nav-cost"
                />
              )}

              {/* Optimize — 13 Killer Features + Tools + Automation */}
              {isGroupVisible(navGroups.optimize) && (
                <MegaMenu
                  label="Optimize"
                  Icon={navGroups.optimize.Icon}
                  accentColor={navGroups.optimize.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.optimize).featured}
                  columns={filterGroupForMegaMenu(navGroups.optimize).columns}
                  footer={navGroups.optimize.footer}
                  testId="nav-optimize"
                />
              )}

              {/* Token Intel — LLM Engineering Suite + Token P&L (second heart of the product) */}
              {isGroupVisible(navGroups.tokenIntel) && (
                <MegaMenu
                  label="Token Intel"
                  Icon={navGroups.tokenIntel.Icon}
                  accentColor={navGroups.tokenIntel.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.tokenIntel).featured}
                  columns={filterGroupForMegaMenu(navGroups.tokenIntel).columns}
                  footer={navGroups.tokenIntel.footer}
                  testId="nav-token-intel"
                  showNewPulse={!tokenIntelDiscovered}
                  onTriggerClick={() => {
                    if (!tokenIntelDiscovered) {
                      localStorage.setItem('token_intel_discovered', '1');
                      setTokenIntelDiscovered(true);
                    }
                  }}
                />
              )}

              {/* Smart Engines — Patent-pending engines, verified by mathematical tests */}
              {isGroupVisible(navGroups.inventions) && (
                <MegaMenu
                  label="Smart Engines"
                  Icon={navGroups.inventions.Icon}
                  accentColor={navGroups.inventions.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.inventions).featured}
                  columns={filterGroupForMegaMenu(navGroups.inventions).columns}
                  footer={navGroups.inventions.footer}
                  testId="nav-smart-engines"
                  align="right"
                />
              )}

              {/* Pilot — Honest FinOps moat: ROI Pledge + Readiness + CFO surfaces */}
              {isGroupVisible(navGroups.pilot) && (
                <MegaMenu
                  label="Pilot"
                  Icon={navGroups.pilot.Icon}
                  accentColor={navGroups.pilot.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.pilot).featured}
                  columns={filterGroupForMegaMenu(navGroups.pilot).columns}
                  footer={navGroups.pilot.footer}
                  testId="nav-pilot"
                  align="right"
                  showNewPulse={true}
                />
              )}

              {/* Manage — Setup, Teams, Infra, Integrations, Admin */}
              {isGroupVisible(navGroups.manage) && (
                <MegaMenu
                  label="Manage"
                  Icon={navGroups.manage.Icon}
                  accentColor={navGroups.manage.accentColor}
                  featured={filterGroupForMegaMenu(navGroups.manage).featured}
                  columns={filterGroupForMegaMenu(navGroups.manage).columns}
                  footer={navGroups.manage.footer}
                  testId="nav-manage"
                  align="right"
                />
              )}

              {/* Company items live in the avatar dropdown — keeps the top bar clean */}
              {/* Snowflake items are consolidated under Cost > Snowflake (no duplicates) */}

              {/* Company Dropdown - About, Careers, Contact */}
              {/* Company items moved to user-avatar dropdown for a cleaner top nav */}
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1.5">
            {/* Search Button — icon-only below 2xl to keep navbar within 1366px laptop viewport */}
            <Button
              variant="ghost"
              onClick={() => setIsCommandPaletteOpen(true)}
              className="flex items-center justify-center 2xl:justify-start gap-2 px-2 2xl:px-3 h-9 w-9 2xl:w-auto text-slate-400 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              data-testid="search-trigger"
              aria-label="Open search"
              title="Search · ⌘K"
            >
              <Search className="w-4 h-4" />
              <span className="hidden 2xl:inline text-sm">Search</span>
            </Button>

            {/* AI Advisor — icon-only below 2xl breakpoint (saves ~80px on common laptops) */}
            <Link
              to="/ai-advisor"
              className={`hidden sm:flex items-center gap-1.5 px-2 2xl:px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all h-9 ${
                location.pathname === '/ai-advisor'
                  ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/30'
                  : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
              }`}
              data-testid="nav-ai-advisor"
              aria-label="AI Advisor"
              title="AI Advisor"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden 2xl:inline">AI Advisor</span>
            </Link>

            {/* Org Switcher (only renders when user has 2+ orgs) — hidden below 2xl for space */}
            <div className="hidden 2xl:block">
              <OrgSwitcher />
            </div>


            {/* Notification Bell */}
            <NotificationBell />

            {/* Settings removed from top bar — available in user menu dropdown for a cleaner look */}

            {/* User Menu — click the avatar to see Profile, Settings, and Sign out */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="flex items-center gap-2 px-1.5 2xl:px-2 h-9 ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-emerald-400 transition-all"
                  data-testid="user-menu-trigger"
                  title="Your account · Sign out"
                >
                  <div className="w-7 h-7 rounded-full bg-emerald-400 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                    <span className="text-xs font-semibold text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden 2xl:inline text-xs font-medium text-slate-400 dark:text-slate-300 max-w-[100px] truncate">
                    {user?.name || 'Account'}
                  </span>
                  <ChevronDown className="w-3 h-3 text-slate-400 hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-400 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                      <span className="font-semibold text-white">
                        {user?.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-slate-400">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="flex items-center justify-between">
                  <span>Role</span>
                  <Badge className={`${getRoleBadgeColor(user?.role, isDemoUser)} capitalize text-xs`}>
                    {isDemoUser ? 'Demo' : user?.role}
                  </Badge>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/pricing" className="flex items-center gap-2 cursor-pointer">
                    <Sparkles className="w-4 h-4" />
                    Upgrade Plan
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="w-4 h-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/api-keys" className="flex items-center gap-2 cursor-pointer" data-testid="menu-api-keys">
                    <FileCode className="w-4 h-4" />
                    API Keys
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/referrals" className="flex items-center gap-2 cursor-pointer" data-testid="menu-referrals">
                    <Sparkles className="w-4 h-4" />
                    Refer a friend
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/developers" className="flex items-center gap-2 cursor-pointer" data-testid="menu-developers">
                    <Code2 className="w-4 h-4" />
                    Developer Docs
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="flex items-center gap-2 cursor-pointer" data-testid="menu-faq">
                    <HelpCircle className="w-4 h-4" />
                    FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/privacy" className="flex items-center gap-2 cursor-pointer">
                    <Shield className="w-4 h-4" />
                    Privacy & Security
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[11px] text-slate-400 uppercase tracking-wider font-normal">
                  Company
                </DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/app/about" className="flex items-center gap-2 cursor-pointer" data-testid="menu-about">
                    <Info className="w-4 h-4" />
                    About
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/careers" className="flex items-center gap-2 cursor-pointer" data-testid="menu-careers">
                    <Briefcase className="w-4 h-4" />
                    Careers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/blog" className="flex items-center gap-2 cursor-pointer" data-testid="menu-blog">
                    <FileBarChart className="w-4 h-4" />
                    Blog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/app/contact" className="flex items-center gap-2 cursor-pointer" data-testid="menu-contact">
                    <Mail className="w-4 h-4" />
                    Contact
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-rose-600 focus:text-rose-600 cursor-pointer font-medium"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Open navigation menu" title="Menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 md:hidden">
                <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">
                  Navigation
                </DropdownMenuLabel>
                {allNavItems.map((item) => (
                  <DropdownMenuItem key={item.path} asChild>
                    <Link 
                      to={item.path} 
                      className={`flex items-center gap-2 ${
                        location.pathname === item.path ? 'text-emerald-600' : ''
                      }`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette 
        isOpen={isCommandPaletteOpen} 
        onClose={() => setIsCommandPaletteOpen(false)} 
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, tier: 'pro', feature: '' })}
        requiredTier={upgradeModal.tier}
        featureName={upgradeModal.feature}
      />

      {/* What's New Modal */}
      <WhatsNewModal open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />

      {/* Pilot Moat Tour — fires once for first-time signed-in users */}
      <PilotMoatTour />
    </nav>
    </>
  );
};

export default Navbar;
